import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X, Trash2, AlertTriangle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { formatCurrency } from '@/lib/utils';

export default function DeleteTaskConfirmModal({ task, isOpen, onConfirm, onCancel, isProcessing }) {
  const { currency: userCurrency } = useAuth();
  const currency = userCurrency || 'USD';
  
  if (!task) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center px-4"
          onClick={onCancel}
        >
          <motion.div
            initial={{ scale: 0.95, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.95, y: 20 }}
            transition={{ type: 'spring', stiffness: 260, damping: 20 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-xs uppercase tracking-wide text-[#EF476F] font-semibold">Delete task</p>
                <h2 className="text-xl font-bold text-gray-900 mt-1">Remove this task permanently?</h2>
              </div>
              <button
                onClick={onCancel}
                className="p-2 rounded-xl hover:bg-gray-100 text-gray-500"
                aria-label="Close delete dialog"
              >
                <X size={20} />
              </button>
            </div>

            <div className="border-2 border-gray-100 rounded-2xl p-4 mb-4 bg-gray-50">
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-xl bg-white shadow-sm">
                  <Trash2 size={20} className="text-[#EF476F]" />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-gray-500">Task</p>
                  <h3 className="text-lg font-semibold text-gray-900">{task.title}</h3>
                  {task.description && (
                    <p className="text-sm text-gray-600 mt-1">{task.description}</p>
                  )}
                  {task.dueDate && (
                    <p className="text-sm text-gray-500 mt-1">
                      Due: {new Date(task.dueDate).toLocaleDateString()}
                    </p>
                  )}
                  {task.isFinancialTask && task.estimatedAmount && (
                    <p className="text-sm font-semibold text-[#EF476F] mt-1">
                      Amount: {formatCurrency(task.estimatedAmount, currency)}
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-start gap-3 mb-4 p-3 bg-amber-50 border border-amber-200 rounded-xl">
              <AlertTriangle size={20} className="text-amber-600 mt-0.5" />
              <p className="text-sm text-amber-900">
                <strong>Warning:</strong> This action cannot be undone. The task will be permanently deleted.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={onCancel}
                className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-2xl text-gray-700 font-semibold hover:bg-gray-50 transition-all"
              >
                Cancel
              </button>
              <button
                onClick={onConfirm}
                disabled={isProcessing}
                className={`flex-1 px-4 py-3 rounded-2xl font-bold text-white shadow-lg transition-all ${
                  isProcessing ? 'bg-gray-400 cursor-not-allowed' : 'bg-[#EF476F] hover:bg-[#d83d61]'
                }`}
              >
                {isProcessing ? 'Deleting…' : 'Yes, delete task'}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
